import {
	checkIfFileExists,
	getAllMdxFilePaths,
	getAllSubFolders,
	getConfig,
} from '@hashnode/file-parsing'
import path from 'path'
import {
	apiReferenceVersionSchema,
	GuideVariant,
	guideVersionSchema,
	ParsedAPIReferenceVersionConfig,
	ParsedConfig,
	ParsedGuideVersionConfig,
	ParsedGuideVersionWithPathConfig,
	ParsedPageConfig,
	ParsedPageWithContent,
	ParsedSectionWithPageContentConfig,
	ParsedSidebarItemConfig,
	ParsedSidebarItemConfigWithPageContent,
} from '../../schemas'
import { generateSlug } from '../generate-slug'
import { getGuideConfig } from '../get-guide-config'
import { parsePageContent } from './parse-page'

function parsePage({
	item,
	rootDir,
	parentPath = '',
}: {
	item: ParsedPageConfig
	rootDir: string
	parentPath?: string
}): ParsedPageWithContent {
	const filePath = path.join(rootDir, item.path!)

	const parsedPageContent = parsePageContent(filePath)

	const slug = generateSlug({
		title: parsedPageContent.title ?? '',
		slug: parsedPageContent.slug ?? '',
	})

	const newPath = `${parentPath}/${slug}`

	// Recursively validate nested pages
	const pages = item.pages?.map((page) => parsePage({ item: page, rootDir, parentPath: newPath }))

	const updatedItem: ParsedPageWithContent = {
		...item,

		...parsedPageContent,
		pages: pages ?? [],
		fullSlug: newPath,
		filePath,
	}

	return updatedItem
}

function parsePages(
	sidebar: ParsedSidebarItemConfig[],
	rootDir: string,
): ParsedSidebarItemConfigWithPageContent[] {
	return sidebar.map((item) => {
		if (item.type === 'page') {
			return parsePage({ item, rootDir, parentPath: '' })
		}

		if (item.type === 'section') {
			if (item.pages && item.pages.length > 0) {
				const slug = generateSlug({
					title: item.label,
					slug: item.slug ?? '',
				})
				// Recursively validate sections
				const updatedPages = item.pages.map((page) =>
					parsePage({ item: page, rootDir, parentPath: `/${slug}` }),
				)

				const updatedSection: ParsedSectionWithPageContentConfig = {
					...item,
					pages: updatedPages,
				}
				return updatedSection
			}
			return {
				...item,
				pages: [],
			}
		}

		// For 'link' or 'section' without pages return the original item
		if (item.type === 'link') {
			return item
		}

		throw new Error(`Invalid item type`)
	})
}

export function parseVersions<T extends ParsedGuideVersionConfig | ParsedAPIReferenceVersionConfig>(
	guideFolderPath: string,
	variant: GuideVariant,
): {
	version: T
	versionFolderPath: string
}[] {
	// Check if there is a single subfolder
	const subfolders = getAllSubFolders(guideFolderPath)

	if (subfolders.length < 1) {
		throw new Error(
			`Expected at least one version in ${guideFolderPath}, but found ${subfolders.length}`,
		)
	}

	const versions: {
		version: T
		versionFolderPath: string
	}[] = []

	for (const subfolder of subfolders) {
		const versionFolderPath = path.join(guideFolderPath, subfolder)
		const configFilePath = path.join(versionFolderPath, 'config.ts')

		// Check if config.ts exists in the subfolder
		if (!checkIfFileExists(configFilePath)) {
			throw new Error(`config.ts not found in ${versionFolderPath}`)
		}

		// Parse the config file
		const configContent = getConfig({ filePath: configFilePath })

		if (!configContent) {
			throw new Error(`Failed to read or parse config.ts in ${versionFolderPath}`)
		}

		if (variant === 'GUIDE') {
			const parsedVersionConfig = guideVersionSchema.parse(configContent)
			versions.push({
				version: parsedVersionConfig as T,
				versionFolderPath,
			})
		} else {
			const parsedApiReferenceConfig = apiReferenceVersionSchema.parse(configContent)
			versions.push({
				version: parsedApiReferenceConfig as T,
				versionFolderPath,
			})
		}
	}

	const defaultVersion = versions.filter(({ version }) => version.settings.isDefault)

	if (defaultVersion.length === 0) {
		throw new Error(`No default version found in ${guideFolderPath}`)
	}

	if (defaultVersion.length > 1) {
		throw new Error(`More than one default version found in ${guideFolderPath}`)
	}

	return versions
}

function getAllPaths(item: ParsedSidebarItemConfigWithPageContent): string[] {
	if (item.type === 'page') {
		const paths = [item.path.replace('./', '')]
		if (item.pages) {
			item.pages.forEach((subPage) => {
				paths.push(...getAllPaths(subPage))
			})
		}
		return paths
	} else if (item.type === 'section' && item.pages) {
		return item.pages.flatMap((page) => getAllPaths(page))
	}
	return []
}

export function parseVersionConfig({
	guideConfig,
	guideFolderPath,
}: {
	guideConfig: ParsedConfig
	guideFolderPath: string
}) {
	if (guideConfig.variant === 'GUIDE') {
		const versions: ParsedGuideVersionWithPathConfig[] = []

		for (const version of parseVersions<ParsedGuideVersionConfig>(guideFolderPath, 'GUIDE')) {
			const { version: parsedVersionConfig, versionFolderPath } = version
			const updateSidebar = parsePages(parsedVersionConfig.sidebar, versionFolderPath)

			const mdxFilePathsInVersionFolder = getAllMdxFilePaths(versionFolderPath)

			const sidebarPagesPaths = updateSidebar.flatMap((item) => getAllPaths(item))

			const hiddenPagePaths = mdxFilePathsInVersionFolder.filter(
				(file) => !sidebarPagesPaths.includes(file),
			)

			const hiddenPages = hiddenPagePaths.map((pagePath) =>
				parsePage({
					item: {
						path: pagePath,
						type: 'page',
						pages: [], // only top level
					},
					rootDir: versionFolderPath,
				}),
			)

			versions.push({
				...parsedVersionConfig,
				sidebar: updateSidebar,
				path: versionFolderPath,
				hiddenPages, // <- add all pages that are not in the sidebar
			})
		}

		return {
			...guideConfig,
			path: guideFolderPath,
			versions: versions,
		}
	} else {
		const versions = parseVersions<ParsedAPIReferenceVersionConfig>(guideFolderPath, 'GUIDE')

		return {
			...guideConfig,
			versions: versions.map(({ version }) => version),
		}
	}
}

export function parseGuideConfig(guideConfigPath: string): ParsedConfig {
	const result = getGuideConfig(guideConfigPath)
	return result
}
